import React from 'react'
import { observer } from 'mobx-react'
import {
  MutableColumnDetails,
  StaticColumnDetails,
} from 'jobs/infra/JobsTableColumns'
import {
  Dropdown,
  DropdownTitle,
  DropdownDivider,
  DropdownItem,
  CheckboxState,
  DropdownScroll,
} from 'components/Dropdown'
import Icon from 'components/Icon'
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
} from 'react-beautiful-dnd'
import cx from 'classnames'
import styles from './TableControls.sass'
import JobsTableColumnManager from 'jobs/infra/JobsTableColumnManager'
import { ColumnDensity } from 'components/Table/TableCell'
import { match } from 'utils/pattern-match-util'
import { pluralize } from 'utils/pluralization-util'
import JobManagementViewModel from '../../JobManagementViewModel'

interface TableControlsProps {
  vm: JobManagementViewModel
  button: React.ReactNode
}

export default observer(function TableControls({
  vm,
  button,
}: TableControlsProps) {
  const columnManager = vm.columnManager
  const exportCSV = columnManager.exportJobsAsCSV
  const selectionVM = vm.selectionVM

  return (
    <>
      <Dropdown autoDismiss position="bottom-end" button={button}>
        <div className={styles.tableControlsContainer}>
          <DropdownTitle>Menu</DropdownTitle>
          <TableControlsItem
            icon={
              <Icon
                className={styles.tableControlsItemIcon}
                name="download"
                size={24}
              />
            }
            onClick={() => exportCSV()}
            dataTest="job-management-table-menu-export-csv"
          >
            Export CSV
          </TableControlsItem>
          <TableControlsItem
            icon={
              <Icon
                className={styles.tableControlsItemIcon}
                name="settings-nav"
                size={24}
              />
            }
            onClick={columnManager.toggleColumnManager}
            dataTest="job-management-table-menu-manage-columns"
          >
            Manage columns
          </TableControlsItem>
          {vm.canNotifyProsOfUnclaimedJobs && (
            <TableControlsItem
              icon={
                <Icon
                  className={styles.tableControlsItemIcon}
                  name="notifications"
                  size={24}
                />
              }
              onClick={() => vm.notifyProsOfUnclaimedJobsVM.show()}
              dataTest="job-management-table-menu-notify-pros-unclaimed"
            >
              Notify Pros of unclaimed jobs
            </TableControlsItem>
          )}
          {selectionVM.selectedJobs.size > 0 && (
            <>
              <DropdownDivider />
              <DropdownTitle>Selection</DropdownTitle>
              {selectionVM.archiveRestoreAction.type !== 'NoJobsSelected' &&
                selectionVM.canArchiveRestoreJobs && (
                  <TableControlsItem
                    icon={match(selectionVM.archiveRestoreAction.type, {
                      Restore: () => (
                        <Icon
                          className={styles.tableControlsItemIcon}
                          name="restore"
                          size={24}
                        />
                      ),
                      Archive: () => (
                        <Icon
                          className={styles.tableControlsItemIcon}
                          name="delete"
                          size={24}
                        />
                      ),
                    })}
                    onClick={selectionVM.archiveRestoreJobs}
                    dataTest="job-management-table-menu-archive-restore-jobs"
                  >
                    {match(selectionVM.archiveRestoreAction.type, {
                      Restore: () => 'Restore archived jobs',
                      Archive: () => 'Archive jobs',
                    })}
                  </TableControlsItem>
                )}
              {selectionVM.canCloneJobs && (
                <TableControlsItem
                  icon={
                    <Icon
                      className={styles.tableControlsItemIcon}
                      name="copy"
                      size={24}
                    />
                  }
                  onClick={selectionVM.cloneJobs}
                  dataTest="job-management-table-menu-clone-jobs"
                >
                  Clone Jobs
                </TableControlsItem>
              )}
              {selectionVM.canTransferJobs && (
                <TableControlsItem
                  icon={
                    <Icon
                      className={styles.tableControlsItemIcon}
                      name="transfer"
                      size={24}
                    />
                  }
                  onClick={selectionVM.transferJobs}
                  dataTest="job-management-table-menu-bulk-transfer-jobs"
                >
                  Transfer{' '}
                  {vm.selectionVM.selectedJobs.size > 1
                    ? `${vm.selectionVM.selectedJobs.size} Jobs`
                    : 'Job'}
                </TableControlsItem>
              )}
              {selectionVM.canAdvanceJobs && (
                <TableControlsItem
                  onClick={selectionVM.advanceJobs}
                  dataTest="job-management-table-menu-advance-jobs"
                  icon={
                    <Icon
                      className={styles.tableControlsItemIcon}
                      name="play"
                      size={24}
                    />
                  }
                >
                  Advance {selectionVM.selectedJobs.size}{' '}
                  {pluralize('job', selectionVM.selectedJobs.size)}
                </TableControlsItem>
              )}
            </>
          )}

          <DropdownDivider />
          <DropdownTitle>Density</DropdownTitle>
          <TableControlsItem
            icon={
              <Icon
                className={cx(
                  styles.tableControlsItemIcon,
                  columnManager.density === ColumnDensity.Default &&
                    styles.activeIcon,
                )}
                name="density-default"
                size={24}
              />
            }
            active={columnManager.density === ColumnDensity.Default}
            onClick={() => vm.columnManager.setDensity(ColumnDensity.Default)}
            dataTest="job-management-table-menu-density-default"
          >
            Default
          </TableControlsItem>
          <TableControlsItem
            icon={
              <Icon
                className={cx(
                  styles.tableControlsItemIcon,
                  columnManager.density === ColumnDensity.Comfortable &&
                    styles.activeIcon,
                )}
                name="density-comfortable"
                size={24}
              />
            }
            active={columnManager.density === ColumnDensity.Comfortable}
            onClick={() =>
              vm.columnManager.setDensity(ColumnDensity.Comfortable)
            }
            dataTest="job-management-table-menu-density-comfortable"
          >
            Comfortable
          </TableControlsItem>
          <TableControlsItem
            icon={
              <Icon
                className={cx(
                  styles.tableControlsItemIcon,
                  columnManager.density === ColumnDensity.Compact &&
                    styles.activeIcon,
                )}
                name="density-compact"
                size={24}
              />
            }
            active={columnManager.density === ColumnDensity.Compact}
            onClick={() => vm.columnManager.setDensity(ColumnDensity.Compact)}
            dataTest="job-management-table-menu-density-compact"
          >
            Compact
          </TableControlsItem>
        </div>
      </Dropdown>
      <ColumnControls
        columnManager={columnManager}
        isOpen={columnManager.columnManagerOpen}
        onRequestClose={columnManager.closeColumnManager}
      />
    </>
  )
})

interface ColumnControlsProps {
  columnManager: JobsTableColumnManager
  isOpen: boolean
  onRequestClose: () => void
}

export const ColumnControls = observer(function ColumnControls({
  columnManager,
  isOpen,
  onRequestClose,
}: ColumnControlsProps) {
  const columns = columnManager.filteredColumns

  return (
    <>
      <Dropdown
        position="bottom-end"
        isOpen={isOpen}
        onRequestClose={onRequestClose}
      >
        <DropdownTitle>Manage columns</DropdownTitle>
        <ColumnControlsItem
          checkboxState={
            columnManager.allColumnsVisible
              ? CheckboxState.Checked
              : CheckboxState.Indeterminate
          }
          onClick={() => columnManager.toggleAllColumnVisibility()}
          dataTest="column-manager-all-columns"
        >
          All Columns
        </ColumnControlsItem>
        <DropdownDivider />
        <DragDropContext
          onDragEnd={(r) =>
            columnManager.updateColumnOrder(
              r.source.index,
              r.destination?.index,
            )
          }
        >
          <Droppable
            droppableId="column-manager"
            renderClone={(provided, snapshot, rubric) => {
              const column = columns[rubric.source.index]
              return (
                <RearrangeableColumn
                  vm={columnManager}
                  column={column}
                  provided={provided}
                  snapshot={snapshot}
                />
              )
            }}
          >
            {(droppableProvided) => (
              <DropdownScroll
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
                {columns.map((column, index) =>
                  !column.actionSelector ? (
                    <Draggable
                      disableInteractiveElementBlocking={true}
                      key={column.id}
                      draggableId={column.id}
                      index={index}
                    >
                      {(draggableProvided) => (
                        <RearrangeableColumn
                          vm={columnManager}
                          column={column}
                          provided={draggableProvided}
                        />
                      )}
                    </Draggable>
                  ) : (
                    <></>
                  ),
                )}
                {droppableProvided.placeholder}
              </DropdownScroll>
            )}
          </Droppable>
        </DragDropContext>
      </Dropdown>
    </>
  )
})

interface TableControlsItemProps {
  children: React.ReactNode
  icon: React.ReactNode
  active?: boolean
  onClick?: () => void
  dataTest?: string
}

const TableControlsItem = ({
  children,
  icon,
  active,
  onClick,
  dataTest,
}: TableControlsItemProps) => (
  <DropdownItem
    icon={icon}
    active={active}
    onClick={onClick}
    dataTest={dataTest}
  >
    {children}
  </DropdownItem>
)

interface RearrangeableColumnProps {
  vm: JobsTableColumnManager
  column: StaticColumnDetails & MutableColumnDetails
  provided?: DraggableProvided
  snapshot?: DraggableStateSnapshot
}

const RearrangeableColumn = observer(function RearrangeableColumn({
  vm,
  column,
  provided,
  snapshot,
}: RearrangeableColumnProps) {
  return (
    <ColumnControlsItem
      draggableProvided={provided}
      checkboxState={
        column.visible ? CheckboxState.Checked : CheckboxState.Unchecked
      }
      visibilityModifiable={column.modifyVisibility}
      elevated={snapshot?.isDragging}
      onClick={() => vm.toggleColumnVisibility(column.id)}
      dataTest={`column-manager-${column.header
        .replace(' ', '-')
        .toLowerCase()}`}
    >
      {column.header}
    </ColumnControlsItem>
  )
})

interface ColumnControlsItemProps {
  children: React.ReactNode
  checkboxState: CheckboxState
  checkboxIndeterminate?: boolean
  visibilityModifiable?: boolean
  elevated?: boolean
  onClick?: () => void
  draggableProvided?: DraggableProvided
  dataTest?: string
}

const ColumnControlsItem = observer(function ColumnControlsItem({
  children,
  checkboxState,
  visibilityModifiable = true,
  draggableProvided,
  elevated,
  onClick,
  dataTest,
}: ColumnControlsItemProps) {
  return (
    <DropdownItem
      ref={draggableProvided?.innerRef}
      {...draggableProvided?.draggableProps}
      {...draggableProvided?.dragHandleProps}
      checkboxState={checkboxState}
      disabled={!visibilityModifiable}
      draggable={Boolean(draggableProvided)}
      isDragging={elevated}
      onClick={onClick}
      dataTest={dataTest}
    >
      {children}
    </DropdownItem>
  )
})
